import { Grid } from "@mui/material";
import { FormProvider, UseFormReturn } from "react-hook-form";

import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../../common/amplitude/useLiveFormAmplitudeEvents";
import { FormStepContainer } from "../../FormStepContainer";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { DeleteConfirmations, deleteConfirmationAnchorSx } from "@/components/DeleteConfirmation/DeleteConfirmation";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { OutlinedTextFieldWysiwyg } from "@/components/OutlinedTextFieldWysiwyg/OutlinedTextFieldWysiwyg";
import { FroalaConfig } from "@/components/OutlinedTextFieldWysiwyg/type";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { containsRaffleWord } from "@/features/LiveFormEditor/common/sweepstakes";
import { StepDescriptionFormData } from "@/features/LiveFormEditor/Steps/StepDescription/shared/types";
import { Add as AddIcon } from "@/icons/outlined";

export type LiveEditorStepDescriptionStatelessProps = {
  form: UseFormReturn<StepDescriptionFormData>;
  wording: {
    stepTitle: string;
    stepDescription: string;
  };
  handleSaveAndNext: () => Promise<void>;
  handleSaveAndBack: () => Promise<void>;
  initialDescription: string | null;
  prefilledText: string;
  forbiddenWordMessage?: string;
};

export const LiveEditorStepDescriptionStateless = ({
  form,
  wording,
  handleSaveAndNext,
  handleSaveAndBack,
  initialDescription,
  prefilledText,
  forbiddenWordMessage,
}: LiveEditorStepDescriptionStatelessProps) => {
  const { t } = useTranslate();
  const { logUserInputEvent, logUserActionEvent } = useLiveFormAmplitudeEvents();
  const { errors } = form.formState;

  const description = form.watch("description");
  const showEditor = description !== null;
  const hadInitialDescription = initialDescription !== null;

  const handleDeleteDescription = () => {
    form.setValue("description", null, { shouldDirty: true });
  };

  const onShowDescriptionClick = () => {
    logUserActionEvent(AmplitudeEvents.FormDescriptionAdded);
    form.setValue("description", prefilledText, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <FormProvider {...form}>
      <FormStepContainer
        title={wording.stepTitle}
        description={wording.stepDescription}
        form={form}
        actionsProps={{
          onBack: handleSaveAndBack,
          onNext: handleSaveAndNext,
          nextButtonLabel: showEditor || hadInitialDescription ? "next" : "skip",
        }}
      >
        {/* Add Description Button */}
        {!showEditor && (
          <DashboardButton
            data-test="add-description-button"
            startIcon={<AddIcon />}
            variant="outlined"
            vibe="brand"
            onClick={onShowDescriptionClick}
          >
            {t("dashboard", "ticketing.addDescription")}
          </DashboardButton>
        )}

        {/* Description Form */}
        {showEditor && (
          <LiveEditorBlock rowGap={2} sx={deleteConfirmationAnchorSx}>
            <Grid container alignItems="center">
              {/* Label */}
              <Grid item xs={11}>
                <DashboardTypography variant="subtitle2">{t("dashboard", "eventDescription")}</DashboardTypography>
              </Grid>
              <Grid item xs={1}>
                <DeleteConfirmations
                  confirmation={handleDeleteDescription}
                  shouldConfirm={!!form.getValues("description")?.trim?.()}
                />
              </Grid>
            </Grid>

            {/* Editor */}
            <OutlinedTextFieldWysiwyg
              id="description"
              name="description"
              keyName="description"
              error={errors["description"]}
              heightMinOver310={310}
              placeholderText={t("dashboard", "donationForm.defaultMobileDescription")}
              defaultValue={form.watch("description") || ""}
              config={FroalaConfig.Minimalist}
              rules={
                forbiddenWordMessage
                  ? [
                      {
                        validate: (value: string | null | undefined) =>
                          !containsRaffleWord(value) || forbiddenWordMessage,
                      },
                    ]
                  : undefined
              }
              onBlur={logUserInputEvent({
                inputName: "description",
                isEmpty: !form.getValues("description")?.trim?.(),
                isValid: !errors["description"],
              })}
            />
          </LiveEditorBlock>
        )}
      </FormStepContainer>
    </FormProvider>
  );
};
